import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';

import { IconButton, Input } from '@/components';

import { RangeSlider } from './RangeSlider';

interface NumberInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  placeholder?: string;
  label?: string;
  showSlider?: boolean;
  allowUndefined?: boolean;
  className?: string;
}

export function NumberInput({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = 'px',
  placeholder,
  label,
  showSlider = false,
  allowUndefined = true,
  className = '',
}: NumberInputProps) {
  const { t } = useTranslation();

  const clamp = useCallback((v: number) => Math.min(max, Math.max(min, v)), [min, max]);

  const handleChange = (newVal: number | undefined) => {
    if (newVal === undefined) {
      if (allowUndefined) onChange(undefined);
      return;
    }
    onChange(clamp(newVal));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' && allowUndefined) {
      handleChange(undefined);
      return;
    }
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) handleChange(parsed);
  };

  const increment = () => handleChange(clamp((value ?? min) + step));
  const decrement = () => handleChange(clamp((value ?? min) - step));

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          {label}
        </label>
      )}
      <div className="flex items-center gap-0.5">
        <IconButton
          variant="subtle"
          size="sm"
          aria-label={t('tenant.systemModuleBuilder.style.common.decrease')}
          onClick={decrement}
          className="h-6 w-5 flex-shrink-0 rounded"
        >
          <Minus className="h-3 w-3" />
        </IconButton>
        <Input
          type="number"
          value={value !== undefined ? value : ''}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          containerClassName="min-w-0 flex-1"
          className="px-1.5 py-1 text-xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          suffixIcon={
            unit ? (
              <span className="select-none text-[10px] text-gray-400 dark:text-gray-500">
                {unit}
              </span>
            ) : undefined
          }
        />
        <IconButton
          variant="subtle"
          size="sm"
          aria-label={t('tenant.systemModuleBuilder.style.common.increase')}
          onClick={increment}
          className="h-6 w-5 flex-shrink-0 rounded"
        >
          <Plus className="h-3 w-3" />
        </IconButton>
      </div>
      {showSlider && (
        <RangeSlider
          min={min}
          max={max}
          step={step}
          value={value ?? min}
          onChange={(v) => handleChange(v)}
          className="mt-1.5 w-full"
          aria-label={label}
        />
      )}
    </div>
  );
}
